<script setup lang="ts">
//#region imports
import { computed, nextTick, ref, h, resolveComponent } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { omit } from 'lodash-es';
import { usePermissions } from '@/composables/usePermissions';
import { useTablePaginator, useTableSearch, useTableSort } from '@/composables/useTableLS';
import { useUpdateUrl } from '@/composables/useUpdateUrl';
import { useApi } from '@/api';
import { usePopupManager } from '@/components/PopupManager.vue';
import { useEventBus } from '@/setup/eventBus';
import { useLodash } from '@/setup/lodash';
import type { UiTableNewField, UiTableNewSortEmit } from '@/types/table';
import type { GetDomainsManagementPlatformServersResponse, GetDomainsManagementPlatformServersData } from '@/types/openapi-ts';
import type { SettingsPageQuery } from '@/types/core';
import type UiPagination from '@/components/UIElements/UiPagination.vue';
//#endregion

//#region types
type PlatformServersQuery = SettingsPageQuery<GetDomainsManagementPlatformServersData['query']>;

type IPVersion = 'v4' | 'v6';

type AddressFields = {
  main: string;
  additional: string;
};
//#endregion

//#region const
const LS_PATH = 'PlatformServers';

const sizes = {
  1080: 20,
  1440: 30,
};

const addressType = {
  v4: {
    main: 'main_ipv4_address',
    additional: 'additional_ipv4_addresses',
  },
  v6: {
    main: 'main_ipv6_address',
    additional: 'additional_ipv6_addresses',
  },
} as const satisfies Record<IPVersion, AddressFields>;

const fields: UiTableNewField<GetDomainsManagementPlatformServersResponse['payload'][number]>[] = [
  {
    name: 'incrementing_id',
    sortable: 'custom',
    align: 'left',
    width: '60',
  },
  {
    name: 'name',
    label: 'server_name',
    sortable: 'custom',
    minWidth: '100',
  },
  {
    name: 'can_be_picked_automatically',
    type: 'status',
    statusText: (value) => t(`domainManagement.fields.can_be_picked_automatically.values.${value}`),
    status: (value) => value ? 'positive' : 'negative',
  },
  {
    name: 'latest_config_request_status',
    type: 'status',
    statusText: (value) => t(`domainManagement.fields.latest_config_request_status.values.${value}`),
    status: (value) => value === 'ok' ? 'positive' : value === 'warning' ? 'negative' : 'unknown',
  },
  {
    name: 'ipv4_addresses_total_count',
    sortable: 'custom',
    linkAppend: (row) => renderIpAddresses(row, 'v4'),
  },
  {
    name: 'ipv4_addresses_available_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
  },
  {
    name: 'ipv6_addresses_total_count',
    sortable: 'custom',
    linkAppend: (row) => renderIpAddresses(row, 'v6'),
  },
  {
    name: 'ipv6_addresses_available_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
  },
  {
    name: 'platform_service_domains_count',
    align: 'right',
    sortable: 'custom',
    link: serviceDomainLink,
    format: 'number',
    class: 'service-domain-link',
  },
  {
    name: 'traffic_last_30_days_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
  },
  {
    name: 'traffic_last_7_days_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
  },
  {
    name: 'traffic_last_day_count',
    align: 'right',
    sortable: 'custom',
    format: 'number',
  },
  {
    name: 'created_at',
    sortable: 'custom',
    width: '120',
    format: 'date-time',
  },
];
//#endregion

//#region composables
const api = useApi();
const route = useRoute();
const { t } = useI18n();
const { hasPermission } = usePermissions();
const { searchQuery } = useTableSearch();
const popupManager = usePopupManager();
const updateUrl = useUpdateUrl();
const eventBus = useEventBus();
const _ = useLodash();
const { limit, count, page, setPage } = useTablePaginator({
  lsPath: LS_PATH,
  sizes,
  defaultSize: 15,
});
const { sortProp, sortOrder, optionsSort } = useTableSort({
  initialOptions:{
    sort_column: 'incrementing_id',
    sort_dir: 'desc',
  },
  lsPath: LS_PATH,
  fields,
});
//#endregion

//#region state
const tableData = ref<GetDomainsManagementPlatformServersResponse['payload']>([]);
const isDataLoading = ref(false);
const isFirstLoading = ref(true);
//#endregion

//#region computed
const canEditPlatformServers = computed(() => {
  return hasPermission('platform_servers_edit');
});
//#endregion

//#region methods
function initData() {
  const _urlData = updateUrl.getParseParams(route.hash);

  if (!_.isEmpty(_urlData)) {
    setPage(_urlData);
    delete _urlData.page;
    delete _urlData.limit;

    _.merge(optionsSort.value, _urlData);
  }
  nextTick(() => {
    fetchData();
  });
}

function _query() {
  return {
    ...optionsSort.value,
    limit: limit.value,
    search: searchQuery.value,
    offset: limit.value * page.value - limit.value,
  } as GetDomainsManagementPlatformServersData['query'];
}

function _completedFilterUrl() {
  const _dataFilters: PlatformServersQuery = omit(_query(), 'offset');
  _dataFilters.page = page.value;
  updateUrl.updateGetParams(_dataFilters);
}

async function fetchData(silent = false) {
  if (!silent && !isFirstLoading.value) {
    isDataLoading.value = true;
  }

  _completedFilterUrl();
  const options = _query();

  try {
    const { data: { payload, misc } } = await api.getPlatformServers(options);

    count.value = misc!.count;
    tableData.value = payload;
  } finally {
    if(!isFirstLoading.value) {
      isDataLoading.value = false;
    } else {
      isFirstLoading.value = false;
    }
  }
}

function handleRefresh () {
  page.value = 1;
  fetchData();
}

const pageChange: InstanceType<typeof UiPagination>['$props']['onPage-change'] = (pageVal, size) => {
  if (page.value !== pageVal || limit.value !== size) {
    page.value = pageVal;
    limit.value = size;
    fetchData();
  }
};

const handleSort = ({ prop, order }: UiTableNewSortEmit) => {
  if (order === '') return;
  if (sortProp.value !== prop) {
    sortProp.value = prop;
  } else {
    sortOrder.value = order;
  }
  nextTick(() => {
    fetchData();
  });
};

async function updateServersConfig() {
  const { status } = await api.updatePlatformServersConfig();
  if (status === 204) {
    eventBus.$emit('notify-event', {
      type: 'info',
      text: t('domainManagement.messages.servers_config_update_success'),
    });
  } else {
    eventBus.$emit('notify-event', {
      type: 'error',
      text: t('domainManagement.messages.servers_config_update_fail'),
    });
  }
}

async function openPlatformServerPopup(id = null) {
  popupManager.platformServerPopup.open({
    data: {
      platformServerId: id,
    },
    listeners: {
      success: (row, isCreate) => {
        if(isCreate) {
          openInitScriptPopup(row.init_commands);
        }
        fetchData(true);
      },
    },
  });
}

function openInitScriptPopup(commands: string[]) {
  popupManager.platformServerInitScriptPopup.open({
    data: {
      initCommands: commands,
    },
  });
}

function handleDelete(row: GetDomainsManagementPlatformServersResponse['payload'][number]) {
  const confirmPopup = popupManager.confirm.open({
    data: {
      title: t('domainManagement.buttons.delete'),
      message: t('domainManagement.messages.delete', { name: row.name}),
      bodyIcon: 'trash-alt',
      confirmIcon: 'trash-alt',
      bodyIconColor: 'var(--text-negative)',
      confirmTitle: t('domainManagement.buttons.delete'),
      confirmType: 'negative',
    },
    listeners: {
      success: async () => {
        const context = await confirmPopup;
        try {
          await api.deletePlatformServer(row.id);
          await fetchData();
        } catch (error) {
          window.console.error(error);
        }
        context.handleClose();
      },
    },
  });
}

function serviceDomainLink(row: GetDomainsManagementPlatformServersResponse['payload'][number]) {
  return row.platform_service_domains_count > 0
    ? `/tools/service-domains?filters={"platform_server_id":"${row.id}"}` : '';
}

function renderIpAddresses(item: GetDomainsManagementPlatformServersResponse['payload'][number], type: IPVersion) {
  const currentAddress = addressType[type];

  const hasAdditional = item[currentAddress.additional]?.length > 0;
  const mainAddress = item[currentAddress.main] || '—';

  if (!hasAdditional) {
    return h('div', {class: 't:truncate'}, mainAddress);
  }

  const Button = resolveComponent('ui-button');
  const UiDropdown = resolveComponent('ui-dropdown');
  const addresses = item[currentAddress.additional].map((addr) => addr.ip_address);
  const ip = h('div', { class: 't:truncate' }, mainAddress);
  const dropdown = h(
    UiDropdown,
    {
      itemsList: addresses,
    },
    {
      default: () => h(Button,  {size: 'small', class: 't:ml-4'}, {
        default: () => `+${item[currentAddress.additional].length}`,
      }),
    },
  );

  return h('div', { class: 't:flex t:items-center t:w-full' }, [ip, dropdown]);
}
//#endregion

//#region hooks
initData();
//#endregion
</script>

<template>
  <div class="platform-servers">
    <UiToolbar>
      <template v-slot:title>
        {{ $t('domainManagement.pageTitle.platformServers') }}
      </template>
      <template v-slot:left-slot>
        <UiInput
          v-model.trim="searchQuery"
          :width="180"
          is-search
          @search="handleRefresh"
        />
      </template>
      <template v-slot:right-slot>
        <UiPagination
          :page="page"
          :page-size="limit"
          :count="count"
          show-size-select
          @page-change="pageChange"
        />
      </template>
      <template v-slot:actions>
        <UiButton
          v-if="hasPermission('platform_servers_can_run_jobs')"
          icon="cogs"
          @click="updateServersConfig"
        >
          {{ $t('domainManagement.buttons.rebuild_servers_config') }}
        </UiButton>
        <UiButton
          v-if="canEditPlatformServers"
          filled
          icon="plus"
          @click="openPlatformServerPopup()"
        >
          {{ $t('domainManagement.buttons.add_platform_server') }}
        </UiButton>
      </template>
    </UiToolbar>
    <UiTableNew
      v-loading="isDataLoading"
      :fields="fields"
      :data="tableData"
      :rows-count="limit"
      :sort="{
        prop: sortProp,
        order: sortOrder,
      }"
      lazy-loading
      is-static
      i18n-path="domainManagement"
      @sort="handleSort"
    >
      <template v-slot:append>
        <ElTableColumn
          align="center"
          width="100"
        >
          <template v-slot:header="scope">
            <div :key="scope.$index">
              {{ $t('settings.tokens.actions') }}
            </div>
          </template>
          <template v-slot="scope">
            <ActionIcon
              icon="code"
              color="var(--text-info)"
              :tooltip="$t('domainManagement.buttons.view_script')"
              @click="openInitScriptPopup(scope.row.init_commands)"
            />
            <ActionIcon
              v-if="canEditPlatformServers"
              icon="pen"
              :tooltip="$t('domainManagement.buttons.edit')"
              @click="openPlatformServerPopup(scope.row.id)"
            />
            <ActionIcon
              v-if="canEditPlatformServers"
              icon="trash-alt"
              color="var(--text-negative)"
              :tooltip="$t('domainManagement.buttons.delete')"
              :disabled="scope.row.platform_service_domains_count > 0"
              @click="handleDelete(scope.row)"
            />
          </template>
        </ElTableColumn>
      </template>
    </UiTableNew>
  </div>
</template>
